// HLR occlusion stage using the Rust/WASM engine: building geometry stays Rust-side behind a handle,
// while props are classified in JS and passed in to participate in occlusion.
import { aabb2New } from "geom/aabb2Types";
import { DIST_EPSILON_SQUARED } from "geom/core";
import type { HLRPolygon } from "geom/hlr/hlrTypes";
import type { Mat4 } from "geom/mat4";
import type { Plane } from "geom/planeTypes";
import { polygonUnion } from "geom/polyclip/polyClip";
import { polyLine2Area, polyLine2ReverseCopy, polyLine2SignedArea } from "geom/polyLine2";
import { polyLine2New, type PolyLine2 } from "geom/polyLine2Types";
import type { FaceGroupBSP } from "lib/house/faceGroupBSP";
import type { WASMState } from "lib/house/plans/assets/loadWasm";
import type { EntityPolyLine } from "lib/house/plans/perspectiveEntities";
import type { LazyPropMesh } from "lib/house/plans/perspectiveTypes";
import { classifyAndConvertLazyProps, type PropClassificationAabb } from "./hlrPropClassification";
import type { HlrEntityData, RenderHLRResult } from "./hlrPropClassification";

// A "wire" is the input format the Rust/WASM HLR engine consumes: a planar 3D polygon as closed
// stride-3 loops (PolyLine3) plus its parametric `Plane` (an `HLRPolygonWire`; see packages/wasm/src/hlr/README.md).
// This flattens our `HLRPolygon` into that shape and pulls the entityId out for index-alignment.
function hlrPolygonToWire(poly: HLRPolygon<HlrEntityData>): {
  loops3D: number[][];
  plane3D: Plane;
  entityId: string;
} {
  return {
    loops3D: poly.loops3D.map((loop) => Array.from(loop)),
    plane3D: poly.plane3D,
    entityId: poly.userData.entityId,
  };
}

export async function renderHLREntityPolygonsWasm({
  faceGroups,
  lazyPropMeshes = [],
  viewMatrix,
  wasm,
  onError,
}: {
  faceGroups: FaceGroupBSP[];
  lazyPropMeshes?: LazyPropMesh[];
  viewMatrix: Mat4;
  wasm: WASMState;
  onError?: (error: Error) => void;
}): Promise<RenderHLRResult> {
  const wasmExports = wasm.wasm;
  if (!wasmExports) {
    return { entityPolylines: [], debugRegions: [], degenerateRegions: [] };
  }

  // Nothing to occlude and nothing to classify — skip the WASM round trip. An empty `faceGroups` array
  // is truthy at the call site, so guard here (the Rust side tolerates empty input; this avoids the work).
  if (faceGroups.length === 0 && lazyPropMeshes.length === 0) {
    return { entityPolylines: [], debugRegions: [], degenerateRegions: [] };
  }

  // `Mat4` is an index-signature interface (no `length`), so it is not `ArrayLike` and cannot be passed
  // to `Array.from` directly; materialize the 16 column-major entries explicitly.
  const viewMatrixArray = Array.from({ length: 16 }, (_, i) => viewMatrix[i]);

  let handle: number;
  try {
    handle = wasmExports.hlrPrepareFromFaceGroups(faceGroups, viewMatrixArray);
  } catch (e) {
    onError?.(
      new Error("HLR: hlrPrepareFromFaceGroups failed; elevation produced no hidden-line output", { cause: e }),
    );
    console.error("hlrPrepareFromFaceGroups WASM error — falling back to empty result:", e);
    return { entityPolylines: [], debugRegions: [], degenerateRegions: [] };
  }

  try {
    // Building entity ids, index-aligned with the building wires (output sourceIndex 0..N-1).
    let buildingEntityIds: string[];
    try {
      buildingEntityIds = wasmExports.hlrHandleEntityIds(handle) as string[];
    } catch (e) {
      onError?.(new Error("HLR: hlrHandleEntityIds failed; elevation produced no hidden-line output", { cause: e }));
      console.error("hlrHandleEntityIds WASM error — falling back to empty result:", e);
      return { entityPolylines: [], debugRegions: [], degenerateRegions: [] };
    }

    // Classify props against the AABBs the handle exposes, then wire them so their annotations
    // participate in occlusion.
    const propWirePolygons: { loops3D: number[][]; plane3D: Plane }[] = [];
    const propEntityIds: string[] = [];
    if (lazyPropMeshes.length > 0) {
      let aabbs: Float64Array;
      try {
        aabbs = wasmExports.hlrHandleAabbs(handle);
      } catch (e) {
        onError?.(new Error("HLR: hlrHandleAabbs failed; elevation produced no hidden-line output", { cause: e }));
        console.error("hlrHandleAabbs WASM error — falling back to empty result:", e);
        return { entityPolylines: [], debugRegions: [], degenerateRegions: [] };
      }
      const nonPropAabbs: PropClassificationAabb[] = [];
      for (let i = 0; i + 5 < aabbs.length; i += 6) {
        nonPropAabbs.push({
          aabb2D: aabb2New(aabbs[i], aabbs[i + 1], aabbs[i + 2], aabbs[i + 3]),
          aabb3D: { minZ: aabbs[i + 4], maxZ: aabbs[i + 5] },
        });
      }
      const propPolygons = await classifyAndConvertLazyProps(lazyPropMeshes, nonPropAabbs, viewMatrix);
      for (const propPoly of propPolygons) {
        const wire = hlrPolygonToWire(propPoly);
        propEntityIds.push(wire.entityId);
        propWirePolygons.push({ loops3D: wire.loops3D, plane3D: wire.plane3D });
      }
    }

    let pieces: Array<{ loops2D: number[][]; sourceIndex: number }>;
    try {
      pieces = wasmExports.hlrRunFromHandle(handle, propWirePolygons, viewMatrixArray) as Array<{
        loops2D: number[][];
        sourceIndex: number;
      }>;
    } catch (e) {
      onError?.(new Error("HLR: hlrRunFromHandle failed; elevation produced no hidden-line output", { cause: e }));
      console.error("hlrRunFromHandle WASM error — falling back to empty result:", e);
      return { entityPolylines: [], debugRegions: [], degenerateRegions: [] };
    }

    // sourceIndex indexes [building wires (0..N), prop wires (N..N+M)].
    const entityIdByIndex = [...buildingEntityIds, ...propEntityIds];

    const polygonsByEntity = new Map<string, PolyLine2[][]>();
    for (const { sourceIndex, loops2D } of pieces) {
      const entityId = entityIdByIndex[sourceIndex];
      if (entityId === undefined) {
        continue;
      }
      const loops: PolyLine2[] = loops2D.filter((l) => l.length >= 6).map((l) => polyLine2New(l));
      if (loops.length === 0) {
        continue;
      }
      const existing = polygonsByEntity.get(entityId);
      if (existing) {
        existing.push(loops);
      } else {
        polygonsByEntity.set(entityId, [loops]);
      }
    }

    const entityPolylines: EntityPolyLine[] = [];
    const debugRegions: PolyLine2[] = [];
    const degenerateRegions: PolyLine2[] = [];
    for (const [entityId, entityPolygons] of polygonsByEntity) {
      // Anonymous geometry is used in occlusion, but shouldn't be included in the annotation pipeline.
      if (entityId === "") {
        continue;
      }
      let unionFailed = false;
      let unioned: PolyLine2[][];
      try {
        unioned = polygonUnion({ pls: entityPolygons });
      } catch (e) {
        // Union failure is not fatal; fall back to original polygons.
        console.warn(e, `HLR-WASM: failed to union visible regions for entity ${entityId}`);
        unioned = entityPolygons;
        unionFailed = true;
      }

      // The clipper can also fail by returning nothing rather than throwing. Visible pieces always
      // have positive area, so an empty union is never a legitimate result — without this the entity
      // would vanish from the annotation pipeline and take its elevation note with it.
      if (unioned.length === 0 && entityPolygons.length > 0) {
        unioned = entityPolygons;
        unionFailed = true;
      }
      // Emit each unioned region's outer boundary. Holes are dropped: downstream
      // placement only consumes outer loops.
      const polylines: PolyLine2[] = [];
      for (const polygon of unioned) {
        const outer = polygon[0];
        if (outer === undefined || polyLine2Area(outer) < DIST_EPSILON_SQUARED) {
          continue;
        }
        // Normalize winding to positive signed area.
        const loop = polyLine2SignedArea(outer) < 0 ? polyLine2ReverseCopy(outer) : outer;
        polylines.push(loop);
        (unionFailed ? degenerateRegions : debugRegions).push(loop);
      }
      if (polylines.length > 0) {
        entityPolylines.push({ polylines, entityId });
      }
    }

    return { entityPolylines, debugRegions, degenerateRegions };
  } finally {
    wasmExports.disposeHlr(handle);
  }
}
